// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useState } from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  withStyles,
  Typography,
  Grid,
} from '@material-ui/core';
import { get } from 'lodash';
import queryString from 'query-string';
import { arrayOf, shape, string, func, bool } from 'prop-types';

import { getConfig } from '../../../../../reducers/portalConfig';
import { getI18n, getI18nFormattedMessage } from '../../../../../utils/intl';
import {
  AlertMessages,
  ErrorContainer,
  FormTextField,
  FormActionButtons,
} from '../../../../../components';
import {
  setAuthSettings,
  updateAuthScheme,
  saveAuthScheme,
} from '../../../../../actions/authenticationSettings';
import {
  hasError,
} from '../../../../../utils';
import Actions from '../../Actions';
import styles from '../../styles';

export const JwtProviderConfiguration = (props) => {
  const {
    classes,
    errors = [],
    authDetails,
    uuid,
    tabValue,
    onCancelDetails,
    hasAttributeMappingFields,
    hasDetailsFields,
    notifyMessages,
  } = props;

  const intl = getI18n(useIntl());
  const queryParam = queryString.parse(get(props, 'location.search'));
  const qParam = get(queryParam, 'authType') ? `?authType=${queryParam.authType}` : '';

  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [discoveryPoint, setDiscoveryPoint] = useState('');
  const [isDiscoveryPoint, setIsDiscoveryPoint] = useState(false);
  const [audience, setAudience] = useState('');
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  useEffect(() => {
    if(tabValue === 'provider-configuration-tab') {
      if(localStorage.getItem('isUnSavedChanges') === 'true') {
        setIsUnsavedChanges(true);
      } else {
        setIsUnsavedChanges(false);
      }
      setDiscoveryPoint(get(authDetails, 'ConfigurationParams.discoveryEndPoint'));
      setAudience(get(authDetails, 'ConfigurationParams.audienceId'));

    }
  }, [authDetails, tabValue]);

  useEffect(() => {
    if(isUnsavedChanges) {
      const hasSave = !hasAttributeMappingFields(authDetails, queryParam) ||
      !hasDetailsFields(authDetails) || hasError(discoveryPoint, true);
      setIsSaveDisabled(hasSave);
    }
  }, [isUnsavedChanges, discoveryPoint, audience, tabValue]);

  const handleChange = (fieldName, value) => {
    switch (fieldName) {
      case 'discoveryPoint': {
        setDiscoveryPoint(value);
        setIsDiscoveryPoint(hasError(value, true));
        break;
      }
      case 'audience': {
        setAudience(value);
        break;
      }
    }
    localStorage.setItem('isUnSavedChanges', true);
    setIsUnsavedChanges(true);
  }


  const onNext = () => {
    const discoveryPointError = hasError(discoveryPoint, true);

    if (discoveryPointError) {
      setIsDiscoveryPoint(discoveryPointError);
    } else {
      setNotificationStatus('');
      setNotificationMessage('');
      const configParams = {
        discoveryEndPoint: discoveryPoint,
        audienceId: audience,
        orgRoleMappingType: get(authDetails, 'ConfigurationParams.orgRoleMappingType'),
      };
      const authType = uuid ? authDetails.authType : 'PORTAL';
      const data = { ...authDetails, ...{
        ConfigurationParams: configParams,
        authMethod: 'JWT_PAPI',
        authType: authType,
        advancedConfigurations: {},
        } };
        props.setAuthSettings(data);
        const url = uuid ? `/publish/settings/authentication/edit/${uuid}/attribute-mapping${qParam}`
         : `/publish/settings/authentication/add/attribute-mapping${qParam}`;
        props.push(url);
    }
  }

  const onSave = () => {
    const configParams = {
        discoveryEndPoint: discoveryPoint,
        audienceId: audience,
        orgRoleMappingType: get(authDetails, 'ConfigurationParams.orgRoleMappingType'),
    };
    const data = { ...authDetails, ...{
      ConfigurationParams: configParams,
      advancedConfigurations: {},
      authMethod: 'JWT_PAPI',
      } };
    if (uuid) {
      props.updateAuthScheme(uuid, data);
    } else {
      props.saveAuthScheme(uuid, data);
    }
  }

  const onCancel = () => {
    onCancelDetails(isUnsavedChanges);
  }

  return(
    <div id="auth-settings-provider-container">
      {notificationMessage &&
        <AlertMessages
          id="auth-settings-details-notifications"
          message={notificationMessage}
          variant={notificationStatus}
          onClose={() => {
            setNotificationMessage('');
            setNotificationStatus('');
          }}
        />
      }
      {errors.length > 0 &&
        <ErrorContainer errors={errors} />
      }
      <Grid container>
        <Grid md={10} item>
          <Typography variant="h1" className={classes.pageTitle}>
            {intl.getI18nMessage('label.auth.provider.configuration')}
          </Typography>
        </Grid>
        <Grid md={2} item>
          <div className={classes.deleteContainer}>
            <Actions
              {...props}
              uuid={uuid}
              notifyMessages={notifyMessages}
              disabled={get(authDetails, 'defaultConfig') }
            />
          </div>
        </Grid>
      </Grid>
      <Grid container spacing={3} className={classes.gridContainer}>
        <Grid md={12} item>
          <Typography variant="h4">
            {getI18nFormattedMessage('label.auth.identity.provider.configuration')}
          </Typography>
          <FormTextField
            id={'auth-settings-pc-jwt-discovery-point'}
            data-apim-test={'auth-settings-pc-jwt-discovery-point'}
            name={getI18nFormattedMessage('label.authorize.discovery.point')}
            value={discoveryPoint}
            error={isDiscoveryPoint}
            placeholder={intl.getI18nMessage('label.authorize.discovery.point.placeholder')}
            handleChange={(value) => handleChange('discoveryPoint', value)}
            helperText={getI18nFormattedMessage('label.authorize.discovery.point.help.text')}
          />
          <FormTextField
            id={'auth-settings-pc-jwt-audience'}
            data-apim-test={'auth-settings-pc-jwt-audience'}
            name={getI18nFormattedMessage('label.authorize.audience')}
            value={audience}
            optional
            handleChange={(value) => handleChange('audience', value)}
            helperText={getI18nFormattedMessage('label.authorize.audience.help.text')}
          />
        </Grid>
      </Grid>
      <FormActionButtons
        onNextClick={onNext}
        nextText={getI18nFormattedMessage('label.next.button')}
        onCancelClick={onCancel}
        showAdditional={true}
        onAdditionalClick={onSave}
        isDisabledAdditional={isSaveDisabled}
        id="auth-settings-provider-config-buttons"
      />
    </div>
  );
}

JwtProviderConfiguration.propTypes = {
  classes: shape({}),
  errors: shape({}),
  assets: arrayOf(shape({})),
  authDetails:shape({}),
  location: shape({}),
  config: shape({}),
  uuid: string,
  notifyMessages: func,
  setStateTabs: func,
  setAuthSettings: func,
  tabValue: string,
  saveAuthScheme: func,
  updateAuthScheme: func,
  onCancelDetails: func,
  push: func,
  hasAttributeMappingFields: func,
  hasDetailsFields: func,
  isOktaIdpFlag: bool,
};

const mapStateToProps = (state) => ({
  config: getConfig(state),
});

const mapDispatchToProps = {
  setAuthSettings,
  saveAuthScheme,
  updateAuthScheme,
};

export default compose(
withStyles(styles),
connect(mapStateToProps, mapDispatchToProps),
)(JwtProviderConfiguration);
